// src/pages/EmployeeMaster.tsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    Spinner,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Progress,
} from 'reactstrap';
import { Clock, Lock } from 'react-feather';
import { Filter, RefreshCw, Eye, Edit, UserX, MapPin, Briefcase, CreditCard, User, Hash, UserCheck, ChevronDown, Upload, FileText, CheckCircle, XCircle, AlertCircle, Download } from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import TableContainer from "../../Components/Common/TableContainer";
import { format } from 'date-fns';
import {
    fetchEmployees,
    updateEmployeeStatus,
    Employee,
    EmployeeFilters
} from '../../api/employee';
import { fetchUnits, Unit } from '../../api/unit';
import { fetchDesignations, Designation } from '../../api/designation';
import { uploadEmployeeExcel } from '../../api/employee'; // You'll need to create this API function
import { toast } from 'react-toastify';
import PullUsersModal from 'Components/pullusermodal';
import { apiClient } from 'helpers/api_helper';

// Status mapping
const STATUS_MAP = {
    1: { label: 'Active', color: 'success' },
    0: { label: 'Inactive', color: 'warning' },
    2: { label: 'Resigned', color: 'secondary' },
    3: { label: 'Terminated', color: 'danger' }
} as const;

type StatusKey = keyof typeof STATUS_MAP;

// Payment mode labels
const PAYMENT_MODE_LABELS: Record<number, string> = {
    1: 'Bank',
    2: 'Cash'
};

// Salary calculation labels
const SALARY_CALCULATION_LABELS: Record<number, string> = {
    1: 'Biometric',
    2: 'Manual'
};

const EmployeeMaster: React.FC = () => {
    const navigate = useNavigate();
    const [pullModal, setPullModal] = useState(false);
    // State
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [pagination, setPagination] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [units, setUnits] = useState<Unit[]>([]);
    const [designations, setDesignations] = useState<Designation[]>([]);
    const [unitsLoading, setUnitsLoading] = useState(false);
    const [designationsLoading, setDesignationsLoading] = useState(false);

    // Filter state
    const [filter, setFilter] = useState<EmployeeFilters>({
        page: 1,
        limit: 10,
        search: '',
        name: '',
        designation_id: undefined,
        unit_id: undefined,
        status: undefined,
        payment_mode: '',
        department: '',
        gender: '',
        min_basic_salary: undefined,
        max_basic_salary: undefined,
        sort_by: 'id',
        sort_order: 'asc'
    });

    // Active filters (applied)
    const [activeFilters, setActiveFilters] = useState<EmployeeFilters>({
        page: 1,
        limit: 10,
        sort_by: 'id',
        sort_order: 'asc'
    });

    // UI State
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
    const [isFiltering, setIsFiltering] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    // Status Modal
    const [statusModal, setStatusModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [newStatus, setNewStatus] = useState<number>(1);
    const [statusLoading, setStatusLoading] = useState(false);
    const [statusError, setStatusError] = useState('');

    // View Modal
    const [viewModal, setViewModal] = useState(false);
    const [viewingEmployee, setViewingEmployee] = useState<Employee | null>(null);

    // Dropdown state - track which one is open
    const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);

    // Upload Modal State
    const [uploadModal, setUploadModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadResult, setUploadResult] = useState<any>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);

    // Change Password Modal State
    const [changePasswordModal, setChangePasswordModal] = useState(false);
    const [selectedEmployeeForPassword, setSelectedEmployeeForPassword] = useState<Employee | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    // Handle change password
    const handleChangePassword = (employee: Employee) => {
        setSelectedEmployeeForPassword(employee);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
        setPasswordSuccess('');
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setChangePasswordModal(true);
        closeAllDropdowns();
    };

    // Confirm change password - Admin force change (no old password required)
    const confirmChangePassword = async () => {
        // Validate
        if (!newPassword || newPassword.length < 6) {
            setPasswordError('Password must be at least 6 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError('Passwords do not match');
            return;
        }

        setChangingPassword(true);
        setPasswordError('');
        setPasswordSuccess('');

        try {
            // ✅ Admin force change password API (no old password required)
            const response = await apiClient.post(
                `/employees/${selectedEmployeeForPassword!.id}/admin-change-password`,
                {
                    new_password: newPassword,
                    confirm_password: confirmPassword,
                }
            );
            console.log("password changed", response)

            if (response.data) {
                setPasswordSuccess(response.data.message || 'Password changed successfully!');
                toast.success('Password changed successfully');

                // Close modal after success
                setTimeout(() => {
                    setChangePasswordModal(false);
                    // Reset form
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordSuccess('');
                    // Refresh employee list if needed
                    fetchEmployeesData(activeFilters);
                }, 1500);
            } else {
                setPasswordError(response.data.message || 'Failed to change password');
            }
        } catch (error: any) {
            console.error('Error changing password:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to change password';
            setPasswordError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setChangingPassword(false);
        }
    };
    const handlePullSuccess = () => {
        // Refresh employee list after successful pull
        fetchEmployeesData(activeFilters);
        toast.success('Users synced successfully!');
    };
    // Fetch units
    const fetchUnitsData = async () => {
        setUnitsLoading(true);
        try {
            const unitsData = await fetchUnits();
            setUnits(unitsData);
        } catch (error) {
            console.error('Error fetching units:', error);
        } finally {
            setUnitsLoading(false);
        }
    };

    // Fetch designations
    const fetchDesignationsData = async () => {
        setDesignationsLoading(true);
        try {
            const designationsData = await fetchDesignations();
            setDesignations(designationsData);
        } catch (error) {
            console.error('Error fetching designations:', error);
        } finally {
            setDesignationsLoading(false);
        }
    };

    // Fetch employees with filters
    const fetchEmployeesData = useCallback(async (filters: EmployeeFilters) => {
        setLoading(true);
        setError('');

        try {
            // Clean filters - remove undefined/null/empty values
            const cleanedFilters: any = {};
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '' && value !== 'all') {
                    cleanedFilters[key] = value;
                }
            });

            const response = await fetchEmployees(cleanedFilters);
            console.log('response for employee', response.data)
            setEmployees(response.data);
            setPagination(response.pagination);
        } catch (error: any) {
            console.error('Error fetching employees:', error);
            setError(error.message || 'Failed to fetch employees');
            setEmployees([]);
            setPagination(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch initial data
    const fetchInitialData = useCallback(async () => {
        try {
            await Promise.all([
                fetchEmployeesData({}),
                fetchUnitsData(),
                fetchDesignationsData()
            ]);
            setIsInitialLoad(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to load initial data');
        }
    }, [fetchEmployeesData]);

    // Initial fetch
    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);


    // Handle filter changes
    const handleFilterChange = (name: string, value: any) => {
        setFilter(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Apply filters
    const applyFilters = () => {
        const newActiveFilters = {
            ...filter,
            page: 1
        };

        setActiveFilters(newActiveFilters);
        fetchEmployeesData(newActiveFilters);

        const hasActiveFilters = Object.entries(filter).some(([key, value]) => {
            if (key === 'page' || key === 'limit' || key === 'sort_by' || key === 'sort_order') {
                return false;
            }
            return value !== '' && value !== undefined && value !== null;
        });

        setIsFiltering(hasActiveFilters);
    };

    // Clear all filters
    const clearFilters = () => {
        const resetFilters: EmployeeFilters = {
            page: 1,
            limit: 10,
            search: '',
            name: '',
            designation_id: undefined,
            unit_id: undefined,
            status: undefined,
            payment_mode: '',
            department: '',
            gender: '',
            min_basic_salary: undefined,
            max_basic_salary: undefined,
            sort_by: 'created_at',
            sort_order: 'desc'
        };

        setFilter(resetFilters);
        setActiveFilters({
            page: 1,
            limit: 10,
            sort_by: 'created_at',
            sort_order: 'desc'
        });
        fetchEmployeesData({
            page: 1,
            limit: 10,
            sort_by: 'created_at',
            sort_order: 'desc'
        });
        setIsFiltering(false);
    };

    // Handle page change
    const handlePageChange = (page: number) => {
        const newActiveFilters = {
            ...activeFilters,
            page: page + 1
        };

        setActiveFilters(newActiveFilters);
        fetchEmployeesData(newActiveFilters);
    };

    // Handle page size change
    const handlePageSizeChange = (size: number) => {
        const newActiveFilters = {
            ...activeFilters,
            limit: size,
            page: 1
        };

        setActiveFilters(newActiveFilters);
        fetchEmployeesData(newActiveFilters);
    };

    // Toggle dropdown - close others and toggle the clicked one
    const toggleDropdown = (employeeId: number) => {
        setOpenDropdownId(prevId => prevId === employeeId ? null : employeeId);
    };

    // Close all dropdowns
    const closeAllDropdowns = () => {
        setOpenDropdownId(null);
    };

    // Handle view employee
    const handleViewEmployee = (employee: Employee) => {
        setViewingEmployee(employee);
        setViewModal(true);
        closeAllDropdowns();
    };

    // Handle edit employee
    const handleEditEmployee = (id: number) => {
        navigate(`/edit-employee/${id}`);
        closeAllDropdowns();
    };
    const handletypeassignEmployee = (id: number) => {
        navigate(`/employees/${id}/type-assignment`);
        closeAllDropdowns();
    };

    // Handle status change (inactive)
    const handleStatusChange = (employee: Employee) => {
        setSelectedEmployee(employee);
        setNewStatus(employee.status === 1 ? 0 : 1); // Toggle active/inactive
        setStatusError('');
        setStatusModal(true);
        closeAllDropdowns();
    };

    // Confirm status change
    const confirmStatusChange = async () => {
        if (!selectedEmployee) return;

        setStatusLoading(true);
        setStatusError('');

        try {
            const statusMap = {
                1: 'active',
                0: 'inactive',
                2: 'resigned',
                3: 'terminated'
            };

            const statusString = statusMap[newStatus as keyof typeof statusMap];

            if (!statusString) {
                throw new Error('Invalid status');
            }

            await updateEmployeeStatus(selectedEmployee.id, {
                status: statusString as 'active' | 'inactive' | 'resigned' | 'terminated'
            });

            // Refresh data
            await fetchEmployeesData(activeFilters);
            setStatusModal(false);
            setSelectedEmployee(null);
        } catch (error: any) {
            setStatusError(error.message || 'Failed to update employee status');
        } finally {
            setStatusLoading(false);
        }
    };

    // Upload Modal Handlers
    const openUploadModal = () => {
        setUploadModal(true);
        setSelectedFile(null);
        setUploadResult(null);
        setUploadError(null);
        setUploadProgress(0);
    };

    const closeUploadModal = () => {
        setUploadModal(false);
        setSelectedFile(null);
        setUploadResult(null);
        setUploadError(null);
        setUploadProgress(0);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            const validTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel'
            ];
            if (!validTypes.includes(file.type)) {
                setUploadError('Please upload a valid Excel file (.xlsx or .xls)');
                setSelectedFile(null);
                return;
            }
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setUploadError('File size should be less than 5MB');
                setSelectedFile(null);
                return;
            }
            setSelectedFile(file);
            setUploadError(null);
            setUploadResult(null);
        }
    };

    // Handle send intro email
    const handleSendIntroEmail = async (employee: Employee) => {
        closeAllDropdowns();

        const toEmail = employee.personal_email || employee.official_email;
        if (!toEmail) {
            toast.error('This employee has no personal or official email on file');
            return;
        }

        const ok = window.confirm(
            `Send intro email to ${employee.name}?\n\nThis will email their login credentials to: ${toEmail}`
        );
        if (!ok) return;

        const toastId = toast.loading('Sending intro email...');

        try {
            const response = await apiClient.post(
                `/employees/${employee.id}/send-intro-email`
            );

            const payload = response?.data ?? response;

            if (payload?.success === false) {
                throw new Error(payload.message || 'Failed to send intro email');
            }

            toast.update(toastId, {
                render: payload?.message || `Intro email sent to ${toEmail}`,
                type: 'success',
                isLoading: false,
                autoClose: 3000,
            });
        } catch (error: any) {
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to send intro email';
            toast.update(toastId, {
                render: msg,
                type: 'error',
                isLoading: false,
                autoClose: 4000,
            });
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setUploadError('Please select a file to upload');
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        setUploadError(null);

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            // Simulate progress (since we can't track actual upload progress with axios easily)
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 200);

            const result = await uploadEmployeeExcel(formData);

            clearInterval(progressInterval);
            setUploadProgress(100);

            setUploadResult(result);
            toast.success('Excel upload successfully');
            // Refresh employee list after successful upload
            await fetchEmployeesData(activeFilters);

            // Close modal after 2 seconds
            setTimeout(() => {
                closeUploadModal();
            }, 500);

        } catch (error: any) {
            console.error('Upload error:', error);
            setUploadError(error.response?.data?.message || error.message || 'Failed to upload employee data');
            setUploadProgress(0);
        } finally {
            setUploading(false);
        }
    };

    // Format date for display
    const formatDate = (dateString?: string | null) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'dd MMM yyyy');
        } catch {
            return 'N/A';
        }
    };

    // Get status badge
    const getStatusBadge = (status: number) => {
        const statusInfo = STATUS_MAP[status as StatusKey];
        if (!statusInfo) {
            return <Badge color="secondary">Unknown</Badge>;
        }
        return <Badge color={statusInfo.color}>{statusInfo.label}</Badge>;
    };

    // Check if bank details are required (ID = 1 is Bank)
    const isBankDetailsRequired = (paymentMode: any) => {
        return parseInt(paymentMode) === 1;
    };

    // Get payment mode label
    const getPaymentModeLabel = (mode?: number) => {
        if (mode === undefined || mode === null) return 'N/A';
        return PAYMENT_MODE_LABELS[mode] || String(mode);
    };

    // Get salary calculation label
    const getSalaryCalculationLabel = (method?: number) => {
        if (method === undefined || method === null) return 'N/A';
        return SALARY_CALCULATION_LABELS[method] || String(method);
    };

    // Table columns
    const columns = useMemo(
        () => [
            {
                header: 'SI.No',
                accessorKey: 'id',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (cell: any) => {
                    const pageSize = activeFilters.limit || 10;
                    const pageIndex = activeFilters.page ? activeFilters.page - 1 : 0;
                    return <strong>{pageIndex * pageSize + cell.row.index + 1}</strong>;
                },
            },
            {
                header: 'Employee Code',
                accessorKey: 'employee_code',
                enableSorting: true,
                cell: (cell: any) => {
                    const employee = cell.row.original;
                    const isOpen = openDropdownId === employee.id;

                    return (
                        <div
                            className="d-flex align-items-center"
                            style={{ gap: '8px' }}
                        >
                            <Dropdown
                                isOpen={isOpen}
                                toggle={() => toggleDropdown(employee.id)}
                                className="d-inline-block"
                            >
                                <DropdownToggle
                                    tag="div"
                                    className="d-flex align-items-center"
                                    style={{
                                        backgroundColor: "#4b1ac9",
                                        color: "#fff",
                                        padding: "5px 10px",
                                        borderRadius: "8px",
                                        fontSize: "12px",
                                        cursor: "pointer",
                                        transition: "all .25s ease",
                                        userSelect: "none",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = "#3d15a3";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = "#4b1ac9";
                                    }}
                                >
                                    <span className="fw-semibold me-1">
                                        {employee.employee_code || "N/A"}
                                    </span>

                                    <ChevronDown
                                        color="#fff"
                                        size={14}
                                        style={{
                                            transition: "transform .25s ease",
                                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                        }}
                                    />
                                </DropdownToggle>

                                <DropdownMenu container="body">
                                    <DropdownItem onClick={() => handleViewEmployee(employee)}>
                                        <Eye size={14} className="me-2" />
                                        View
                                    </DropdownItem>

                                    <DropdownItem onClick={() => handleEditEmployee(employee.id)}>
                                        <Edit size={14} className="me-2" />
                                        Edit
                                    </DropdownItem>

                                    <DropdownItem onClick={() => handletypeassignEmployee(employee.id)}>
                                        <MapPin size={14} className="me-2" />  {/* Changed icon */}
                                        Assign Type & Location
                                    </DropdownItem>
                                    <DropdownItem onClick={() => handleSendIntroEmail(employee)}>
                                        <FileText size={14} className="me-2" />
                                        Send Intro Email
                                    </DropdownItem>
                                    {/* ✅ NEW: Change Password Option */}
                                    <DropdownItem onClick={() => handleChangePassword(employee)}>
                                        {/* <i className="ri-lock-password-line me-2" style={{ fontSize: '14px' }}></i> */}
                                        {/* OR if you want to use react-feather icons: */}
                                        <Lock size={14} className="me-2" />
                                        Change Password
                                    </DropdownItem>

                                    <DropdownItem
                                        onClick={() => handleStatusChange(employee)}
                                        className={employee.status === 1 ? 'text-danger' : 'text-success'}
                                    >
                                        {employee.status === 1 ? (
                                            <>
                                                <UserX size={14} className="me-2" />
                                                Deactivate
                                            </>
                                        ) : (
                                            <>
                                                <UserCheck size={14} className="me-2" />
                                                Activate
                                            </>
                                        )}
                                    </DropdownItem>
                                </DropdownMenu>

                            </Dropdown>
                        </div >
                    );
                },
            },
            {
                header: 'Name',
                accessorKey: 'name',
                enableSorting: false,
                cell: (cell: any) => {
                    const employee = cell.row.original;

                    return (
                        <span className="fw-semibold">
                            {employee.name}
                        </span>
                    );
                },
            },
            {
                header: 'Designation',
                accessorKey: 'designation.title',
                cell: (cell: any) => cell.row.original.designation?.title || 'N/A',
            },
            {
                header: 'Unit',
                accessorKey: 'unit.name',
                cell: (cell: any) => cell.row.original.unit?.name || 'N/A',
            },
            {
                header: 'Basic Salary',
                accessorKey: 'basic_salary',
                cell: (cell: any) => {
                    const value = cell.getValue();
                    return value ? `₹${value.toLocaleString('en-IN')}` : 'N/A';
                },
            },
            {
                header: 'PF & ESI',
                accessorKey: 'pf_esi_applicable',
                cell: (cell: any) => {
                    const value = cell.getValue();
                    return value === 1 ? 'Yes' : 'No';
                },
            },
            {
                header: 'Status',
                accessorKey: 'status',
                cell: (cell: any) => getStatusBadge(cell.getValue()),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeFilters.limit, activeFilters.page, openDropdownId, handleViewEmployee, handleEditEmployee, handleStatusChange]
    );

    // Loading state
    if ((loading && isInitialLoad) || unitsLoading || designationsLoading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Employee Master" pageTitle="Employees" />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardBody className="text-center py-5">
                                    <Spinner color="primary" className="me-2" />
                                    <span>Loading employees...</span>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Employee Master" pageTitle="Employees" />

                {/* Filter Section */}
                <Row className="mb-3">
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0">Filter Options</h5>
                                <div className="d-flex gap-2">
                                    <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => setPullModal(true)}
                                    >
                                        <Download size={16} className="me-1" />
                                        Pull Users
                                    </Button>
                                    <Button
                                        color="success"
                                        size="sm"
                                        onClick={openUploadModal}
                                    >
                                        <Upload size={16} className="me-1" />
                                        Upload Excel
                                    </Button>
                                    <Button
                                        color="light"
                                        size="sm"
                                        onClick={clearFilters}
                                        disabled={!isFiltering}
                                    >
                                        Clear Filters
                                    </Button>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                                    >
                                        <Filter size={16} className="me-1" />
                                        {filterDropdownOpen ? 'Hide Filters' : 'Show Filters'}
                                    </Button>
                                </div>
                            </CardHeader>

                            {filterDropdownOpen && (
                                <CardBody className="py-3">
                                    {/* Main Filter Row */}
                                    <Row className="g-3 align-items-end">
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label-sm mb-1">Search</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Name, Email, Code..."
                                                    value={filter.search || ''}
                                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                                    bsSize="sm"
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label-sm mb-1">Designation</Label>
                                                <Input
                                                    type="select"
                                                    value={filter.designation_id || ''}
                                                    onChange={(e) => handleFilterChange('designation_id', e.target.value ? Number(e.target.value) : undefined)}
                                                    bsSize="sm"
                                                >
                                                    <option value="">All Designations</option>
                                                    {designations?.map((designation) => (
                                                        <option key={designation.id} value={designation.id}>
                                                            {designation.title}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>

                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label-sm mb-1">Company</Label>
                                                <Input
                                                    type="select"
                                                    value={filter.unit_id || ''}
                                                    onChange={(e) => handleFilterChange('unit_id', e.target.value ? Number(e.target.value) : undefined)}
                                                    bsSize="sm"
                                                >
                                                    <option value="">All Companies</option>
                                                    {units?.map((unit) => (
                                                        <option key={unit.id} value={unit.id}>
                                                            {unit.name}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>

                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label-sm mb-1">Status</Label>
                                                <Input
                                                    type="select"
                                                    value={filter.status !== undefined ? filter.status : ''}
                                                    onChange={(e) => handleFilterChange('status', e.target.value !== '' ? Number(e.target.value) : undefined)}
                                                    bsSize="sm"
                                                >
                                                    <option value="">All Status</option>
                                                    <option value="1">Active</option>
                                                    <option value="0">Inactive</option>
                                                    <option value="2">Resigned</option>
                                                    <option value="3">Terminated</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    {/* Advanced Filters */}
                                    <div className="mt-3 pt-3 border-top">
                                        <Row className="g-3">
                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label className="form-label-sm mb-1">Department</Label>
                                                    <Input
                                                        type="text"
                                                        placeholder="Enter department..."
                                                        value={filter.department || ''}
                                                        onChange={(e) => handleFilterChange('department', e.target.value)}
                                                        bsSize="sm"
                                                    />
                                                </FormGroup>
                                            </Col>

                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label className="form-label-sm mb-1">Gender</Label>
                                                    <Input
                                                        type="select"
                                                        value={filter.gender || ''}
                                                        onChange={(e) => handleFilterChange('gender', e.target.value)}
                                                        bsSize="sm"
                                                    >
                                                        <option value="">All</option>
                                                        <option value="Male">Male</option>
                                                        <option value="Female">Female</option>
                                                        <option value="Other">Other</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>

                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label className="form-label-sm mb-1">Payment Mode</Label>
                                                    <Input
                                                        type="select"
                                                        value={filter.payment_mode || ''}
                                                        onChange={(e) => handleFilterChange('payment_mode', e.target.value)}
                                                        bsSize="sm"
                                                    >
                                                        <option value="">All</option>
                                                        <option value="cash">Cash</option>
                                                        <option value="rtgs">RTGS</option>
                                                        <option value="neft">NEFT</option>
                                                        <option value="upi">UPI</option>
                                                        <option value="bank_transfer">Bank Transfer</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>

                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label className="form-label-sm mb-1">Min Basic Salary</Label>
                                                    <Input
                                                        type="number"
                                                        placeholder="Min basic salary..."
                                                        value={filter.min_basic_salary || ''}
                                                        onChange={(e) => handleFilterChange('min_basic_salary', e.target.value ? Number(e.target.value) : undefined)}
                                                        bsSize="sm"
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="mt-3 pt-3 border-top">
                                        <div className="d-flex justify-content-end gap-2">
                                            <Button
                                                color="light"
                                                size="sm"
                                                onClick={() => fetchEmployeesData(activeFilters)}
                                            >
                                                <RefreshCw size={14} className="me-1" />
                                                Refresh
                                            </Button>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={applyFilters}
                                            >
                                                <Filter size={14} className="me-1" />
                                                Apply Filters
                                            </Button>
                                        </div>
                                    </div>
                                </CardBody>
                            )}
                        </Card>
                    </Col>
                </Row>

                {/* Error Alert */}
                {error && (
                    <Row className="mb-3">
                        <Col lg={12}>
                            <Alert color="danger">
                                <i className="ri-error-warning-line align-middle me-2"></i>
                                {error}
                            </Alert>
                        </Col>
                    </Row>
                )}

                {/* Data Table */}
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0">Employee List</h5>
                                <div className="d-flex gap-2">
                                    <span className="text-muted small align-self-center">
                                        Total: {pagination?.totalCount || 0} employees
                                    </span>
                                </div>
                            </CardHeader>

                            <CardBody>
                                {loading && !isInitialLoad ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" className="me-2" />
                                        <span>Loading employees...</span>
                                    </div>
                                ) : (!employees || employees.length === 0) ? (
                                    <div className="text-center py-5">
                                        <i className="ri-user-line display-4 text-muted"></i>
                                        <h5 className="mt-3">No employees found</h5>
                                        <p className="text-muted">
                                            {isFiltering
                                                ? "No employees match your filter criteria."
                                                : "No employees have been added yet."}
                                        </p>
                                    </div>
                                ) : (
                                    <TableContainer
                                        columns={columns}
                                        data={employees}
                                        customPageSize={activeFilters.limit}
                                        tableClass="table-striped"
                                        theadClass="table-light"
                                        manualPagination={true}
                                        pageCount={pagination?.totalPages || 1}
                                        onPageChange={handlePageChange}
                                        onPageSizeChange={handlePageSizeChange}
                                        currentPage={(activeFilters.page || 1) - 1}
                                        totalRecords={pagination?.totalCount || 0}
                                    />
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Upload Excel Modal */}
            <Modal
                isOpen={uploadModal}
                toggle={closeUploadModal}
                size="lg"
                backdrop="static"
                keyboard={!uploading}
            >
                <ModalHeader toggle={!uploading ? closeUploadModal : undefined}>
                    <div className="d-flex align-items-center">
                        <Upload size={18} className="me-2" />
                        <span>Upload Employee Excel</span>
                    </div>
                </ModalHeader>
                <ModalBody>
                    {uploadResult ? (
                        // Success/Result View
                        <div>
                            <Alert color={uploadResult.errors && uploadResult.errors.length > 0 ? 'warning' : 'success'}>
                                <div className="d-flex align-items-start">
                                    {uploadResult.errors && uploadResult.errors.length > 0 ? (
                                        <AlertCircle size={20} className="me-2 flex-shrink-0 mt-1" />
                                    ) : (
                                        <CheckCircle size={20} className="me-2 flex-shrink-0 mt-1" />
                                    )}
                                    <div>
                                        <strong>{uploadResult.message}</strong>
                                        <div className="mt-1">
                                            <span className="badge bg-success me-1">Inserted: {uploadResult.inserted}</span>
                                            <span className="badge bg-info me-1">Updated: {uploadResult.updated}</span>
                                            {uploadResult.skipped > 0 && (
                                                <span className="badge bg-warning me-1">Skipped: {uploadResult.skipped}</span>
                                            )}
                                            {uploadResult.errors && uploadResult.errors.length > 0 && (
                                                <span className="badge bg-danger">Errors: {uploadResult.errors.length}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Alert>

                            {uploadResult.errors && uploadResult.errors.length > 0 && (
                                <div className="mt-3">
                                    <h6 className="text-danger">Error Details:</h6>
                                    <div className="table-responsive" style={{ maxHeight: '200px', overflow: 'auto' }}>
                                        <table className="table table-sm table-bordered">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Row</th>
                                                    <th>Employee Code</th>
                                                    <th>Error</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {uploadResult.errors.map((err: any, idx: number) => (
                                                    <tr key={idx}>
                                                        <td>{err.row}</td>
                                                        <td>{err.emp_code}</td>
                                                        <td className="text-danger">{err.message}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        // Upload Form View
                        <>
                            <Alert color="info" className="mb-3">
                                <FileText size={16} className="me-2" />
                                Upload an Excel file (.xlsx or .xls) with employee data.
                                The system will update existing employees and create new ones based on employee code.
                            </Alert>

                            <Form>
                                <FormGroup>
                                    <Label>Select Excel File <span className="text-danger">*</span></Label>
                                    <Input
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={handleFileChange}
                                        disabled={uploading}
                                    />
                                    <small className="text-muted">
                                        File should have columns: empcode, name, designation, unit, salary, etc.
                                        Max size: 5MB
                                    </small>
                                </FormGroup>

                                {selectedFile && (
                                    <div className="p-2 bg-light rounded d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <FileText size={16} className="me-2 text-primary" />
                                            <span>{selectedFile.name}</span>
                                        </div>
                                        <span className="text-muted small">
                                            {(selectedFile.size / 1024).toFixed(1)} KB
                                        </span>
                                    </div>
                                )}

                                {uploadError && (
                                    <Alert color="danger" className="mt-3">
                                        <XCircle size={16} className="me-2" />
                                        {uploadError}
                                    </Alert>
                                )}

                                {uploading && (
                                    <div className="mt-3">
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-muted small">Uploading...</span>
                                            <span className="text-muted small">{uploadProgress}%</span>
                                        </div>
                                        <Progress value={uploadProgress} color="primary" animated />
                                    </div>
                                )}
                            </Form>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    {uploadResult ? (
                        <Button color="secondary" onClick={closeUploadModal}>
                            Close
                        </Button>
                    ) : (
                        <>
                            <Button color="light" onClick={closeUploadModal} disabled={uploading}>
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                onClick={handleUpload}
                                disabled={!selectedFile || uploading}
                            >
                                {uploading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <Upload size={14} className="me-1" />
                                        Upload Employees
                                    </>
                                )}
                            </Button>
                        </>
                    )}
                </ModalFooter>
            </Modal>

            {/* View Employee Modal */}
            {/* View Employee Modal */}
            <Modal
                isOpen={viewModal}
                toggle={() => setViewModal(!viewModal)}
                size="xl"
                className="view-employee-modal"
                scrollable
                style={{ maxHeight: '100vh' }}
            >
                <ModalHeader
                    toggle={() => setViewModal(!viewModal)}
                    className="bg-white border-0 py-2"
                >
                    <div className="d-flex align-items-center">
                        <User color="black" size={18} className="me-2" />
                        <span style={{ color: 'black' }}>Employee Details</span>
                    </div>
                </ModalHeader>

                <ModalBody className="p-3">
                    {viewingEmployee && (() => {
                        // Small helper that renders a value or "—" when null/empty
                        const v = (val: any, fallback = '—') => {
                            if (val === null || val === undefined) return fallback;
                            const s = String(val).trim();
                            return s.length ? s : fallback;
                        };

                        const vCurrency = (val: any) =>
                            val !== null && val !== undefined && !isNaN(Number(val))
                                ? `₹${Number(val).toLocaleString('en-IN')}`
                                : '—';

                        const vBool = (val: any) => {
                            if (val === null || val === undefined) return '—';
                            return val ? 'Yes' : 'No';
                        };

                        const vPaymentMode = (mode: any) => {
                            if (mode === null || mode === undefined) return '—';
                            const map: Record<number, string> = {
                                1: 'Bank Transfer',
                                2: 'Cash',
                                3: 'Cheque',
                            };
                            return map[Number(mode)] ?? String(mode);
                        };

                        const vSalaryCalc = (mode: any) => {
                            if (mode === null || mode === undefined) return '—';
                            const map: Record<number, string> = {
                                1: 'Biometric',
                                2: 'Manual',
                            };
                            return map[Number(mode)] ?? String(mode);
                        };

                        const vEmployeeType = (t: any) => {
                            if (t === null || t === undefined) return '—';
                            const map: Record<number, string> = {
                                1: 'Permanent',
                                2: 'Contract',
                                3: 'Probation',
                                4: 'Intern',
                            };
                            return map[Number(t)] ?? String(t);
                        };

                        const vStatus = (s: any) => {
                            if (s === null || s === undefined) return '—';
                            const map: Record<number, string> = {
                                1: 'Active',
                                0: 'Inactive',
                                2: 'Resigned',
                                3: 'Terminated',
                            };
                            return map[Number(s)] ?? String(s);
                        };

                        const vDate = (d: any) => {
                            if (!d) return '—';
                            try {
                                const dt = new Date(d);
                                if (isNaN(dt.getTime())) return '—';
                                return dt.toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                });
                            } catch {
                                return '—';
                            }
                        };

                        const vDateTime = (d: any) => {
                            if (!d) return '—';
                            try {
                                const dt = new Date(d);
                                if (isNaN(dt.getTime())) return '—';
                                return dt.toLocaleString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                });
                            } catch {
                                return '—';
                            }
                        };

                        const emp = viewingEmployee as any;

                        const Row2 = ({
                            label,
                            value,
                            mono = false,
                        }: {
                            label: string;
                            value: React.ReactNode;
                            mono?: boolean;
                        }) => (
                            <div className="d-flex justify-content-between py-1 border-bottom border-light">
                                <span
                                    className="text-muted"
                                    style={{ fontSize: 'clamp(9px, 0.7vw, 11px)' }}
                                >
                                    {label}
                                </span>
                                <span
                                    className={`fw-semibold text-end text-truncate ${mono ? 'font-monospace' : ''
                                        }`}
                                    style={{ maxWidth: '60%' }}
                                    title={typeof value === 'string' ? value : undefined}
                                >
                                    {value}
                                </span>
                            </div>
                        );

                        const SectionCard = ({
                            icon,
                            title,
                            children,
                            fullWidth = false,
                        }: {
                            icon: React.ReactNode;
                            title: string;
                            children: React.ReactNode;
                            fullWidth?: boolean;
                        }) => (
                            <div
                                className={`border rounded-3 p-2 h-100 ${fullWidth ? 'w-100' : ''
                                    }`}
                                style={{ backgroundColor: '#f8f9fa' }}
                            >
                                <h6
                                    className="text-muted mb-1 d-flex align-items-center"
                                    style={{
                                        fontSize: 'clamp(10px, 0.8vw, 12px)',
                                        fontWeight: 600,
                                    }}
                                >
                                    {icon}
                                    <span className="ms-1">{title}</span>
                                </h6>
                                <div style={{ fontSize: 'clamp(10px, 0.8vw, 12px)' }}>
                                    {children}
                                </div>
                            </div>
                        );

                        return (
                            <div className="d-flex flex-column" style={{ gap: 8 }}>
                                {/* ─── Header ─── */}
                                <div className="d-flex align-items-center pb-2 border-bottom flex-wrap">
                                    <div
                                        className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{ width: 44, height: 44, minWidth: 44 }}
                                    >
                                        <User size={22} className="text-primary" />
                                    </div>
                                    <div className="ms-3 flex-grow-1" style={{ minWidth: 120 }}>
                                        <h5
                                            className="mb-0 fw-semibold"
                                            style={{ fontSize: 'clamp(13px, 1.1vw, 15px)' }}
                                        >
                                            {v(emp.name)}
                                        </h5>
                                        <div className="d-flex flex-wrap gap-1 mt-1">
                                            <span
                                                className="badge bg-secondary bg-opacity-10 text-secondary px-2 py-1 rounded-pill"
                                                style={{ fontSize: 'clamp(9px, 0.7vw, 11px)' }}
                                            >
                                                <Hash size={10} className="me-1" />
                                                {v(emp.employee_code)}
                                            </span>
                                            <span
                                                className="badge bg-primary bg-opacity-10 text-primary px-2 py-1 rounded-pill"
                                                style={{ fontSize: 'clamp(9px, 0.7vw, 11px)' }}
                                            >
                                                <Briefcase size={10} className="me-1" />
                                                {v(emp.designation?.title)}
                                            </span>
                                            <span
                                                className="badge bg-info bg-opacity-10 text-info px-2 py-1 rounded-pill"
                                                style={{ fontSize: 'clamp(9px, 0.7vw, 11px)' }}
                                            >
                                                <MapPin size={10} className="me-1" />
                                                {v(emp.unit?.name)}
                                            </span>
                                            {getStatusBadge(emp.status)}
                                        </div>
                                    </div>
                                </div>

                                {/* ─── Personal + Employment ─── */}
                                <Row className="g-2">
                                    <Col md={6}>
                                        <SectionCard
                                            icon={<User size={12} className="flex-shrink-0" />}
                                            title="Personal Information"
                                        >
                                            <Row2 label="Full Name" value={v(emp.name)} />
                                            <Row2 label="Employee Code" value={v(emp.employee_code)} />
                                            <Row2 label="Father's Name" value={v(emp.father_name)} />
                                            <Row2 label="Mother's Name" value={v(emp.mother_name)} />
                                            <Row2 label="Gender" value={v(emp.gender)} />
                                            <Row2 label="Date of Birth" value={vDate(emp.date_of_birth)} />
                                            <Row2 label="Aadhaar" value={v(emp.aadharr)} mono />
                                            <Row2 label="Phone" value={v(emp.phone)} />
                                            <Row2
                                                label="Alternate Phone"
                                                value={v(emp.phone_alternate)}
                                            />
                                            <Row2
                                                label="Personal Email"
                                                value={v(emp.personal_email)}
                                            />
                                            <Row2
                                                label="Official Email"
                                                value={v(emp.official_email)}
                                            />
                                        </SectionCard>
                                    </Col>

                                    <Col md={6}>
                                        <SectionCard
                                            icon={
                                                <Briefcase size={12} className="flex-shrink-0" />
                                            }
                                            title="Employment Details"
                                        >
                                            <Row2
                                                label="Designation"
                                                value={v(emp.designation?.title)}
                                            />
                                            <Row2 label="Unit" value={v(emp.unit?.name)} />
                                            <Row2
                                                label="Date of Joining"
                                                value={vDate(emp.date_of_joining)}
                                            />
                                            <Row2
                                                label="Date of Exit"
                                                value={vDate(emp.date_of_exit)}
                                            />
                                            <Row2
                                                label="Employee Type"
                                                value={vEmployeeType(emp.employee_type)}
                                            />
                                            <Row2 label="Status" value={vStatus(emp.status)} />
                                            <Row2
                                                label="Paid Leaves"
                                                value={v(emp.paid_leaves, '0')}
                                            />
                                            <Row2
                                                label="Overtime"
                                                value={vBool(emp.overtime)}
                                            />
                                            <Row2
                                                label="Geofence"
                                                value={vBool(emp.geofence)}
                                            />
                                            <Row2
                                                label="Face Detection"
                                                value={vBool(emp.facedetection)}
                                            />
                                            <Row2
                                                label="PWA"
                                                value={vBool(emp.is_pwa)}
                                            />
                                        </SectionCard>
                                    </Col>
                                </Row>

                                {/* ─── Address ─── */}
                                <SectionCard
                                    icon={<MapPin size={12} className="flex-shrink-0" />}
                                    title="Address"
                                    fullWidth
                                >
                                    <Row2 label="Address" value={v(emp.address)} />
                                    <Row2 label="City" value={v(emp.city)} />
                                    <Row2 label="State" value={v(emp.state)} />
                                    <Row2 label="Pincode" value={v(emp.pincode)} />
                                </SectionCard>

                                {/* ─── Salary + Statutory ─── */}
                                <Row className="g-2">
                                    <Col md={6}>
                                        <SectionCard
                                            icon={
                                                <CreditCard size={12} className="flex-shrink-0" />
                                            }
                                            title="Salary Details"
                                        >
                                            <Row2
                                                label="Basic Salary"
                                                value={vCurrency(emp.basic_salary)}
                                            />
                                            <Row2
                                                label="Take Home Salary"
                                                value={vCurrency(emp.take_home_salary)}
                                            />
                                            <Row2
                                                label="Site Allowance"
                                                value={vCurrency(emp.site_allowance)}
                                            />
                                            <Row2
                                                label="PF / ESI Applicable"
                                                value={
                                                    emp.pf_esi_applicable === null ||
                                                        emp.pf_esi_applicable === undefined
                                                        ? '—'
                                                        : emp.pf_esi_applicable === 1
                                                            ? 'Yes'
                                                            : 'No'
                                                }
                                            />
                                            <Row2
                                                label="PF / ESI Gross"
                                                value={vCurrency(emp.pf_esi_gross_salary)}
                                            />
                                            <Row2
                                                label="PF Number"
                                                value={v(emp.pf_number)}
                                                mono
                                            />
                                            <Row2
                                                label="ESI Number"
                                                value={v(emp.esi_number)}
                                                mono
                                            />
                                            <Row2
                                                label="Payment Mode"
                                                value={vPaymentMode(emp.payment_mode)}
                                            />
                                            <Row2
                                                label="Salary Calculation"
                                                value={vSalaryCalc(emp.salary_calculation)}
                                            />
                                        </SectionCard>
                                    </Col>

                                    <Col md={6}>
                                        <SectionCard
                                            icon={
                                                <CreditCard size={12} className="flex-shrink-0" />
                                            }
                                            title="Bank Details"
                                        >
                                            <Row2 label="Bank Name" value={v(emp.bank_name)} />
                                            <Row2
                                                label="Account Number"
                                                value={v(emp.bank_account_no)}
                                                mono
                                            />
                                            <Row2
                                                label="IFSC Code"
                                                value={v(emp.ifsc_code)}
                                                mono
                                            />
                                            <Row2 label="Branch" value={v(emp.bank_branch)} />
                                            <Row2
                                                label="Bank Master ID"
                                                value={v(emp.bank_id)}
                                            />
                                        </SectionCard>
                                    </Col>
                                </Row>

                                {/* ─── System / Timestamps ─── */}
                                <SectionCard
                                    icon={<Clock size={12} className="flex-shrink-0" />}
                                    title="System Information"
                                    fullWidth
                                >
                                    <Row className="g-0">
                                        <Col md={6} className="pe-md-2">
                                            <Row2
                                                label="Employee ID"
                                                value={v(emp.id)}
                                                mono
                                            />
                                            <Row2
                                                label="Designation ID"
                                                value={v(emp.designation_id)}
                                                mono
                                            />
                                            <Row2
                                                label="Unit ID"
                                                value={v(emp.unit_id)}
                                                mono
                                            />
                                            <Row2
                                                label="Bank ID"
                                                value={v(emp.bank_id)}
                                                mono
                                            />
                                        </Col>
                                        <Col md={6} className="ps-md-2">
                                            <Row2
                                                label="Created At"
                                                value={vDateTime(emp.created_at)}
                                            />
                                            <Row2
                                                label="Updated At"
                                                value={vDateTime(emp.updated_at)}
                                            />
                                            <Row2
                                                label="Last Login"
                                                value={vDateTime(emp.last_login)}
                                            />
                                            <Row2
                                                label="Login Attempts"
                                                value={v(emp.login_attempts, '0')}
                                            />
                                        </Col>
                                    </Row>
                                </SectionCard>
                            </div>
                        );
                    })()}
                </ModalBody>

                <ModalFooter className="border-0 bg-light py-2">
                    <Button
                        color="secondary"
                        size="sm"
                        onClick={() => setViewModal(false)}
                        className="px-3"
                    >
                        Close
                    </Button>
                    {viewingEmployee && (
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => {
                                setViewModal(false);
                                handleEditEmployee(viewingEmployee.id);
                            }}
                            className="px-3"
                        >
                            <Edit size={14} className="me-1" />
                            Edit
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
            {/* Status Change Confirmation Modal */}
            <Modal isOpen={statusModal} toggle={() => setStatusModal(!statusModal)}>
                <ModalHeader toggle={() => setStatusModal(!statusModal)}>
                    Confirm Status Change
                </ModalHeader>
                <ModalBody>
                    {statusError && (
                        <Alert color="danger" className="mb-3">
                            {statusError}
                        </Alert>
                    )}
                    <p>
                        Are you sure you want to <strong>{newStatus === 1 ? 'activate' : 'deactivate'}</strong>
                        {' '}{selectedEmployee?.name}
                    </p>
                    {newStatus === 0 && (
                        <Alert color="warning" className="mb-0">
                            <i className="ri-alert-line me-2"></i>
                            This employee will no longer have access to the system.
                        </Alert>
                    )}
                    {newStatus === 1 && (
                        <Alert color="success" className="mb-0">
                            <i className="ri-check-line me-2"></i>
                            This employee will regain access to the system.
                        </Alert>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setStatusModal(false)} disabled={statusLoading}>
                        Cancel
                    </Button>
                    <Button
                        color={newStatus === 1 ? 'success' : 'warning'}
                        onClick={confirmStatusChange}
                        disabled={statusLoading}
                    >
                        {statusLoading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Updating...
                            </>
                        ) : (
                            newStatus === 1 ? 'Activate' : 'Deactivate'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
            {/* Change Password Modal */}
            <Modal isOpen={changePasswordModal} toggle={() => setChangePasswordModal(false)}>
                <ModalHeader toggle={() => setChangePasswordModal(false)}>
                    <Lock size={18} className="me-2" />
                    Change Password
                </ModalHeader>
                <ModalBody>
                    {selectedEmployeeForPassword && (
                        <div className="mb-3">
                            <p className="text-muted small">
                                Changing password for: <strong>{selectedEmployeeForPassword.name}</strong>
                                <br />
                                <span className="text-muted">Employee Code: {selectedEmployeeForPassword.employee_code}</span>
                            </p>
                        </div>
                    )}

                    {passwordError && (
                        <Alert color="danger" className="mb-3">
                            <i className="ri-error-warning-line me-2"></i>
                            {passwordError}
                        </Alert>
                    )}

                    {passwordSuccess && (
                        <Alert color="success" className="mb-3">
                            <i className="ri-check-line me-2"></i>
                            {passwordSuccess}
                        </Alert>
                    )}

                    <Form>
                        <FormGroup>
                            <Label className="fw-bold text-muted small text-uppercase">New Password</Label>
                            <div className="input-group">
                                <Input
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);
                                        setPasswordError('');
                                        setPasswordSuccess('');
                                    }}
                                    placeholder="Enter new password (min 6 chars)"
                                    disabled={!!passwordSuccess}
                                />
                                <Button
                                    type="button"
                                    color="secondary"
                                    outline
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    disabled={!!passwordSuccess}
                                >
                                    <i className={showNewPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                                </Button>
                            </div>
                            <small className="text-muted">Password must be at least 6 characters</small>
                        </FormGroup>

                        <FormGroup>
                            <Label className="fw-bold text-muted small text-uppercase">Confirm Password</Label>
                            <div className="input-group">
                                <Input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        setPasswordError('');
                                        setPasswordSuccess('');
                                    }}
                                    placeholder="Confirm new password"
                                    disabled={!!passwordSuccess}
                                />
                                <Button
                                    type="button"
                                    color="secondary"
                                    outline
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    disabled={!!passwordSuccess}
                                >
                                    <i className={showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                                </Button>
                            </div>
                        </FormGroup>

                        {/* Password strength indicator */}
                        {newPassword && !passwordSuccess && (
                            <div className="mt-2">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="flex-grow-1">
                                        <div className="progress" style={{ height: '4px' }}>
                                            <div
                                                className="progress-bar"
                                                style={{
                                                    width: `${Math.min((newPassword.length / 12) * 100, 100)}%`,
                                                    backgroundColor: newPassword.length >= 6 ? '#28a745' : newPassword.length >= 4 ? '#ffc107' : '#dc3545'
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <span className="small text-muted">
                                        {newPassword.length >= 6 ? '✓ Strong' : newPassword.length >= 4 ? '⚠️ Weak' : '❌ Too short'}
                                    </span>
                                </div>
                            </div>
                        )}
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="light"
                        onClick={() => setChangePasswordModal(false)}
                        disabled={changingPassword}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={confirmChangePassword}
                        disabled={changingPassword || !!passwordSuccess}
                    >
                        {changingPassword ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Changing...
                            </>
                        ) : (
                            <>
                                <Lock size={14} className="me-1" />
                                Change Password
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
            <PullUsersModal
                isOpen={pullModal}
                toggle={() => setPullModal(false)}
                onSuccess={handlePullSuccess}
            />
        </div>
    );
};

export default EmployeeMaster;